import type { TablerIcon } from '@tabler/icons-react';
import {
  IconAbc,
  IconAlphabetCyrillic,
  IconAtom,
  IconBook2,
  IconCake,
  IconCalendar,
  IconChartBar,
  IconClock,
  IconDeviceLaptop,
  IconDna,
  IconFileText,
  IconFlask,
  IconHeartHandshake,
  IconHistory,
  IconLanguage,
  IconMathSymbols,
  IconMoodSmile,
  IconMusic,
  IconPalette,
  IconRun,
  IconSchool,
  IconSpeakerphone,
  IconStar,
  IconTools,
  IconTrophy,
  IconUsersGroup,
  IconWorld,
} from '@tabler/icons-react';

/**
 * KIOSK IKONKALARI — slayd belgilari, fan ikonkalari va nishonlash grafikasi.
 *
 * MANBA: `@tabler/icons-react` (MIT), front `package.json` da ALLAQACHON bor.
 * Yangi bog'liqlik YO'Q, CDN YO'Q — 24/7 ishlaydigan TV tashqi xostga bog'lanmaydi
 * (shrift bilan bir xil tamoyil). Nomli importlar Vite tomonidan tree-shake qilinadi.
 *
 * QOIDA: IKONKA HECH QACHON YOLG'IZ TURMAYDI — har doim matn yorlig'i bilan
 * (3-8 metrdan ikonka o'z-o'zidan o'qilmaydi). Shuning uchun barcha ikonkalar
 * dekorativ: `aria-hidden`.
 */

// ── Slayd turlari ──────────────────────────────────────────────────────────
/** Aylanmadagi slayd turlari (Kiosk.tsx `Slide` uyushmasi shu ro'yxatga tayanadi). */
export type KSlideKind =
  | 'top'
  | 'birthdays'
  | 'leaders'
  | 'classActivity'
  | 'schedule'
  | 'weekStar'
  | 'banner';

/**
 * Har slaydning o'z belgisi: bola 6 metrdan sarlavhani O'QIMASDAN ham qaysi
 * slayd ekanini biladi (aeroport tablosi mantiqi).
 */
export const SLIDE_ICON: Record<KSlideKind, TablerIcon> = {
  top: IconTrophy,
  birthdays: IconCake,
  leaders: IconUsersGroup,
  classActivity: IconChartBar,
  schedule: IconClock,
  weekStar: IconStar,
  banner: IconSpeakerphone,
};

// ── Ikonka plitkasi ────────────────────────────────────────────────────────
/**
 * Umumiy ikonka plitkasi — ilova `IconBadge(solid=false)` shaklining aynan o'zi:
 * `--k-soft` fon + `--k-brand-dark` glif (5.59:1, WCAG AA ✓). O'lchov `em` da,
 * ya'ni plitka joylashgan matn qatoriga o'zi moslashadi (kiosk.css `.k-ico`).
 *
 * `stroke`: plitka 1.3em dan kichik bo'lsa 2.4 — ingichka chiziq 6 metrdan
 * yo'qoladi.
 */
export function KIco({ icon: Icon, stroke = 2, className }: {
  icon: TablerIcon; stroke?: number; className?: string;
}) {
  return (
    <span className={className ? `k-ico ${className}` : 'k-ico'} aria-hidden="true">
      <Icon stroke={stroke} />
    </span>
  );
}

// ── Ball yo'nalishlari ─────────────────────────────────────────────────────
/** Reyting bali qaysi 5 yo'nalishdan yig'ilishi (backend `breakdown` kalitlari). */
export type KBallPart = 'grades' | 'attendance' | 'behavior' | 'exams' | 'homework';

/**
 * "Nega u birinchi?" savoliga javob beradigan izoh qatorlari. TOP-10 slaydidagi
 * bo'sh katakda va "Hafta yulduzi" kartasining yonida ishlatiladi.
 */
export const BALL_PARTS: { key: KBallPart; label: string; icon: TablerIcon }[] = [
  { key: 'grades', label: 'Fanlar', icon: IconSchool },
  { key: 'attendance', label: 'Davomat', icon: IconCalendar },
  // Tabler'da `IconMood` yo'q — eng yaqin va neytral variant `IconMoodSmile`.
  { key: 'behavior', label: 'Xulq', icon: IconMoodSmile },
  { key: 'exams', label: 'Testlar', icon: IconFileText },
  { key: 'homework', label: 'Uy vazifasi', icon: IconBook2 },
];

// ── Fan ikonkasi ───────────────────────────────────────────────────────────
/**
 * Fan nomini ikonkaga moslash. Nomni maktab admini kiritadi, shuning uchun
 * qidiruv ATAYLAB erkin: o'zbekcha va ruscha nomlar, katta/kichik harf, har xil
 * apostroflar (o'zbek `ʻ ʼ ' '`) bir shaklga keltiriladi.
 *
 * TARTIB MUHIM: ro'yxatdagi BIRINCHI mos kelgan kalit so'z g'olib chiqadi —
 * aniqrog'i yuqorida turadi ("jismoniy tarbiya" `tarbiya` dan oldin uchraydi,
 * shuning uchun u sport ikonkasini oladi, `odob`/`tarbiya` ikonkasini emas).
 */
const SUBJECT_ICONS: [RegExp, TablerIcon][] = [
  [/matematika|algebra|geometr|hisob|матем|алгебр|геометр/, IconMathSymbols],
  [/informatika|dasturlash|kompyuter|axborot|информат|программир|компьютер/, IconDeviceLaptop],
  [/ingliz|english|англ/, IconLanguage],
  [/rus tili|rus tili va|русск/, IconAlphabetCyrillic],
  [/ona tili|o'zbek|adabiyot|til va adab|узбек|литератур|родн/, IconAbc],
  [/tarix|history|истор/, IconHistory],
  [/fizika|astronom|физик|астроном/, IconAtom],
  [/kimyo|хими/, IconFlask],
  [/biolog|tabiat|ekolog|биолог|природ|эколог/, IconDna],
  [/geograf|географ/, IconWorld],
  [/jismoniy|sport|физкультур|физич/, IconRun],
  [/musiqa|ashula|музык|пени/, IconMusic],
  [/tasviriy|chizmachilik|san'at|rasm|черчен|рисован|изобразит|искусств/, IconPalette],
  [/texnologiya|mehnat|технолог|труд/, IconTools],
  [/odob|tarbiya|huquq|ma'naviyat|воспитан|прав|этик/, IconHeartHandshake],
];

/** Turli apostroflar → bitta `'`; katta harf va ortiqcha bo'shliqlar olib tashlanadi. */
function normalizeSubject(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/[ʻʼ‘’`´′]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Kesh: jadval slaydida eng yomon holatda 5 ustun x 8 qator x 4 sahifa = 160 ta
 * katak, ular har render'da qayta hisoblanmasin. Kalitlar — maktabning fan
 * nomlari (amalda o'nlab), shuning uchun hajm cheklangan; baribir zaxira chegara
 * qo'yilgan.
 */
const iconCache = new Map<string, TablerIcon>();

export function subjectIcon(name: string): TablerIcon {
  const key = normalizeSubject(name);
  const hit = iconCache.get(key);
  if (hit) return hit;
  const found = SUBJECT_ICONS.find(([re]) => re.test(key))?.[1] ?? IconBook2;
  if (iconCache.size > 400) iconCache.clear();
  iconCache.set(key, found);
  return found;
}

// ── Gulchambar (1-o'rin avatari ortida) ────────────────────────────────────
/**
 * NEGA GULCHAMBAR: oltin kubok/medal ilovaning qoidasini buzadi (u yerda 1-o'rin
 * OLTIN emas, to'ldirilgan BREND KO'K). Gulchambar — rangsiz, brend ohangidagi
 * chiziq: rasmiy, "diplom" hissi beradi, o'yinchoq emas. O'zim chizganim uchun
 * litsenziya masalasi ham yo'q.
 */
type Pt = [number, number];

const LAUREL_ARCS: { d: string; pts: [Pt, Pt, Pt, Pt]; side: 1 | -1 }[] = [
  {
    d: 'M100 186 C52 176 22 138 26 86',
    pts: [[100, 186], [52, 176], [22, 138], [26, 86]],
    side: -1,
  },
  {
    d: 'M100 186 C148 176 178 138 174 86',
    pts: [[100, 186], [148, 176], [178, 138], [174, 86]],
    side: 1,
  },
];

/** Kubik Bezier nuqtasi va urinma burchagi (gradusda). */
function cubicAt(p: [Pt, Pt, Pt, Pt], t: number): { x: number; y: number; angle: number } {
  const u = 1 - t;
  const x = u * u * u * p[0][0] + 3 * u * u * t * p[1][0] + 3 * u * t * t * p[2][0] + t * t * t * p[3][0];
  const y = u * u * u * p[0][1] + 3 * u * u * t * p[1][1] + 3 * u * t * t * p[2][1] + t * t * t * p[3][1];
  const dx = 3 * u * u * (p[1][0] - p[0][0]) + 6 * u * t * (p[2][0] - p[1][0]) + 3 * t * t * (p[3][0] - p[2][0]);
  const dy = 3 * u * u * (p[1][1] - p[0][1]) + 6 * u * t * (p[2][1] - p[1][1]) + 3 * t * t * (p[3][1] - p[2][1]);
  return { x, y, angle: (Math.atan2(dy, dx) * 180) / Math.PI };
}

const LEAF_COUNT = 7;
const LEAF_T0 = 0.14;
const LEAF_T1 = 0.92;
/** Barg uzun o'qi yoyning urinmasidan TASHQARIGA shuncha gradus buriladi. */
const LEAF_TILT = 32;

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Barglar bir marta — modul yuklanganda — hisoblanadi (har render'da emas).
 * `opacity` uchiga tomon 0.95 → 0.6 ga so'nadi: gulchambar "o'sib" tugagandek
 * ko'rinadi, lekin hech narsa harakatlanmaydi. Pastki chegara 0.45 EMAS —
 * unda uchdagi barglar 6 metrdan butunlay yo'qolib ketardi.
 */
const LAUREL_LEAVES = LAUREL_ARCS.flatMap(({ pts, side }, arcIdx) =>
  Array.from({ length: LEAF_COUNT }, (_, i) => {
    const t = LEAF_T0 + (LEAF_T1 - LEAF_T0) * (i / (LEAF_COUNT - 1));
    const { x, y, angle } = cubicAt(pts, t);
    return {
      id: `${arcIdx}-${i}`,
      cx: round2(x),
      cy: round2(y),
      rot: round2(angle + side * LEAF_TILT),
      op: round2(0.95 - (0.95 - 0.6) * (i / (LEAF_COUNT - 1))),
    };
  }),
);

/** STATIK SVG — hech qanday animatsiya yo'q (24/7 ekranda bu muhim). */
export function KLaurel() {
  return (
    <svg
      className="k-laurel"
      viewBox="0 0 200 200"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* Chiziq va barglar 6 metr uchun yo'g'onlashtirilgan (5→7, 13x6.5→15x7.5):
          eski qiymatlarda gulchambar TV'da yengil dog'dek ko'rinardi. */}
      {LAUREL_ARCS.map((a) => (
        <path key={a.d} d={a.d} strokeWidth="7" opacity="0.85" />
      ))}
      {LAUREL_LEAVES.map((l) => (
        <ellipse
          key={l.id}
          cx={l.cx}
          cy={l.cy}
          rx="15"
          ry="7.5"
          fill="currentColor"
          stroke="none"
          opacity={l.op}
          transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
        />
      ))}
    </svg>
  );
}
